import type {
    EnquirerType,
    EnquiryAudienceEnum,
    EnquiryFormDestination,
    EnquiryFormFieldType,
    EnquiryFormKind,
    EnquiryFormTrigger,
    EnquiryProjectKind,
    EnquiryProjectTypeEnum,
    PrimaryCtaEnum,
} from 'generated/graphql';
import type {
    FieldCondition,
    FormTheme,
    SuccessModeValue,
} from '~/modules/business-manager/forms/types';
import { EnquiryFormPageType } from 'generated/graphql';
import type { UseEnquiryProps } from '../hooks/use-enquiry';

/** One selectable "What is your enquiry about?" option. */
export interface PublicEnquiryFormType {
    id: number;
    title: string;
}

/**
 * A routing rule's conditions and outcome, without recipients. Enough for the form to
 * tell the visitor a meeting will be offered before they submit.
 */
export interface PublicEnquiryFormRoutingRule {
    id: number;
    enquiryFormTypeIds: number[];
    enquirerTypes: EnquirerType[];
    projectKinds: EnquiryProjectKind[];
    countries: string[];
    regions: string[];
    destination: EnquiryFormDestination;
    /** Set when the destination is an active meeting session. */
    calendarSessionId: number | null;
    /** Shown instead of sending when the destination is Message. */
    message: string | null;
    /** Conditions on custom Select fields (Website forms). */
    fieldConditions: FieldCondition[];
}

/** A custom question on a Website form. */
export interface PublicEnquiryFormField {
    id: number;
    key: string;
    label: string;
    type: EnquiryFormFieldType;
    required: boolean;
    placeholder: string;
    options: string[];
    /** Choice fields only: an "Other" choice reveals a text box for a free answer. */
    allowOther: boolean;
    width: 'Half' | 'Full';
}

/** One branch the visitor can send the enquiry to. */
export interface PublicEnquiryFormBranchOption {
    id: number;
    title: string;
    /** The branch's region as a routing slug (see `regionSlug`), when it has one. */
    regionSegment: string | null;
}

/** The form's "Which branch would you like to contact?" question. */
export interface PublicEnquiryFormBranchPicker {
    enabled: boolean;
    required: boolean;
    label: string;
    /** Preselect the branch whose region matches the location answer or IP region. */
    preselectByLocation: boolean;
    options: PublicEnquiryFormBranchOption[];
}

/** The public shape of a professional's enquiry form. Never carries recipients. */
export interface PublicEnquiryForm {
    id: number;
    title: string;
    kind: EnquiryFormKind;
    audience: EnquiryAudienceEnum;
    projectType: EnquiryProjectTypeEnum;
    ctaLabel: PrimaryCtaEnum;
    customMessage: string | null;
    messageOverrideEnabled: boolean;
    messagePlaceholder: string | null;
    /** False when the form only collects details and has no message box. */
    messageEnabled: boolean;
    /** False when the message box may be left blank (Website forms only). */
    messageRequired: boolean;
    /** Whether the attach-files control is shown. */
    attachmentsEnabled: boolean;
    enquiryTypesEnabled: boolean;
    enquirerTypeEnabled: boolean;
    projectKindQuestionEnabled: boolean;
    locationEnabled: boolean;
    routingEnabled: boolean;
    defaultDestination: EnquiryFormDestination;
    calendarSession: {
        id: number;
        sessionName: string;
        meetingType: string;
        isActive: boolean;
        hasConnectedConsultant: boolean;
    } | null;
    enquiryTypes: PublicEnquiryFormType[];
    routingRules: PublicEnquiryFormRoutingRule[];
    /** Website forms only. */
    fields: PublicEnquiryFormField[];
    locationLabel: string;
    heading: string;
    submitLabel: string;
    consentText: string;
    theme: FormTheme;
    successMode: SuccessModeValue;
    successMessage: string;
    successUrl: string;
    /**
     * The branch question. `undefined` when the API does not describe one, in
     * which case the page's own branch list (from the professional's network)
     * still drives the picker.
     */
    branchPicker?: PublicEnquiryFormBranchPicker | null;
}

/**
 * What a CTA's Forms lookup resolved to. `legacy` keeps the flat professional
 * settings, `off` means the button is switched off for this page, `form`
 * carries the configured form.
 */
export interface EnquiryFormConfig {
    status: 'legacy' | 'off' | 'form';
    form: PublicEnquiryForm | null;
}

/** A signed, expiring link to a file the form hands out after submit. */
export interface EnquiryDownloadLink {
    id: number;
    title: string;
    url: string;
    expiresAt: string;
    fileName: string | null;
    fileSize: number;
    isExternal: boolean;
}

/**
 * Whether the form must collect details before it can offer a booking. With
 * routing rules on, the destination depends on the answers, so a "Book a
 * meeting" button cannot jump straight to a calendar; the enquiry form runs
 * first and hands off to the booking step when a rule lands on a booking link.
 */
export const booksAfterDetails = (
    form: Pick<PublicEnquiryForm, 'routingEnabled' | 'routingRules'>,
): boolean => form.routingEnabled && form.routingRules.length > 0;

/**
 * The visitor's choices on a form, sent with the enquiry so routing and
 * booking requests can be honoured server-side.
 */
export interface EnquiryFormSubmissionContext {
    enquiryFormId: number;
    enquiryFormTypeId?: number;
    enquirerType?: EnquirerType;
    projectKind?: EnquiryProjectKind;
    requestBooking?: boolean;
    channel?:
        | 'archipro'
        | 'website_embed'
        | 'book_meeting_member'
        | 'book_meeting_company';
    /** Answers to the form's custom fields, by key. */
    fieldAnswers?: Record<string, string>;
    /** Host page an embedded form was on. */
    sourceUrl?: string;
    /** UTM / click-id parameters from the host page. */
    sourceParams?: Record<string, string>;
    /** Turnstile token from the embed page; verified server-side when configured. */
    captchaToken?: string;
    /**
     * `inline` asks the submit route to return a routing decline as data so
     * the form can show it, instead of flashing it as an error toast.
     */
    declineHandling?: 'inline';
}

/** Maps a public page's library item type onto the Forms page-type axis. */
export const PAGE_TYPE_BY_ITEM_TYPE: Record<
    UseEnquiryProps['itemType'],
    EnquiryFormPageType
> = {
    article: EnquiryFormPageType.Article,
    product: EnquiryFormPageType.Product,
    professional: EnquiryFormPageType.Professional,
    project: EnquiryFormPageType.Project,
};

export type { EnquiryFormPageType, EnquiryFormTrigger };
